import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";

import { v4 as uuid } from "uuid";

import { Observable, Subscription } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, loadedTenantState } from "src/app/store/reducers";

import { MatSelect, MatSelectChange } from "@angular/material/select";
import { MatMenuTrigger } from "@angular/material/menu";

import {
  TableConfig,
  FieldType,
  AdvancedFilter,
  ColumnFilterInfo,
  FilterName,
  NUMBER_OPERATORS,
  STRING_OPERATORS,
  DATE_OPERATORS,
  BOOLEAN_OPERATORS,
} from "src/app/models/custom-table/custom-table.model";

@Component({
  selector: "table-advanced-filter, button[table-advanced-filter]",
  templateUrl: "./advanced-filter.component.html",
  styleUrls: ["./advanced-filter.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterComponent implements OnInit, OnDestroy {
  @ViewChild(MatMenuTrigger) filterMenu: MatMenuTrigger;
  @ViewChildren(MatSelect) matSelects!: QueryList<MatSelect>;

  @Input() columns: string[];
  @Input() columnNames: string[];
  @Input() set tableConfiguration(tableConfig: TableConfig[]) {
    this.tableConfig = tableConfig;
    this.setFilterColumns();
  }
  @Input() filterQuantity: number;

  @Output()
  advancedFilterChanged: EventEmitter<AdvancedFilter[]> = new EventEmitter<
    AdvancedFilter[]
  >();

  filters: AdvancedFilter[] = [];
  filterableColumns: ColumnFilterInfo[];

  operators: string[];
  stringOperators = STRING_OPERATORS;
  numberOperators = NUMBER_OPERATORS;
  dateOperators = DATE_OPERATORS;
  booleanOperators = BOOLEAN_OPERATORS;

  fieldType = FieldType;

  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  tenantData: any;

  dateTimeFormat: string;

  tableConfig: TableConfig[];

  isApplyEnabled: boolean = false;

  constructor(private store: Store<AppState>) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
  }

  ngOnInit(): void {
    this.loadTenantDateTimeFomat();
    this.setFilterColumns();
    this.addDefaultFilter();
    document.addEventListener('keydown', this.handleEnterKeyPress.bind(this));
  }

  loadTenantDateTimeFomat() {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.dateTimeFormat = data.datetimeFormat;
      }
    });
  }

  setFilterColumns(): void {
    const columnInfo = this.setColumnInfo(this.columns);
    this.filterableColumns = this.setfilterableDisplayedColumns(columnInfo);
  }

  setColumnInfo(displayedColumns: string[]): ColumnFilterInfo[] {
    return displayedColumns.map((column, index) => {
      return {
        id: column,
        name: this.columnNames[index],
      };
    });
  }

  setfilterableDisplayedColumns(
    displayedColumns: ColumnFilterInfo[]
  ): ColumnFilterInfo[] {
    return displayedColumns.filter((column) =>
      this.tableConfig.some(
        (colConfig) =>
          colConfig.field === column.id &&
          colConfig.fieldType !== FieldType.OPTIONS &&
          colConfig.fieldType !== FieldType.PROGRESS &&
          colConfig.fieldType !== FieldType.ARRAY &&
          !colConfig.noAdvanceFiltering
      )
    );
  }

  addDefaultFilter(): void {
    const [firstColumn] = this.filterableColumns;
    const { id } = firstColumn;

    const defaultFieldType = this.setFieldType(id, this.tableConfig);
    const defaultOperator = this.setDefaultOperator(defaultFieldType);

    this.filters = [
      ...this.filters,
      {
        id: uuid(),
        column: id,
        fieldType: defaultFieldType,
        operator: defaultOperator,
        value: "",
      },
    ];
    this.checkValue();
  }

  setDefaultOperator(fieldType: FieldType): FilterName {
    switch (fieldType) {
      case FieldType.STRING:
      case FieldType.CHIP_STRING:
      case FieldType.ICON:
      case FieldType.COLOR:
      case FieldType.OBJECT:
      case FieldType.OBJECT_DESCRIPTION:
      case FieldType.IMAGE:
      case FieldType.COUNTRY:
        const [firstStringOperator] = this.stringOperators;
        return firstStringOperator;
      case FieldType.NUMBER:
      case FieldType.CHIP_NUMBER:
        const [firstNumberOperator] = this.numberOperators;
        return firstNumberOperator;
      case FieldType.DATE:
      case FieldType.DATE_TIME:
        const [firstDateOperator] = this.dateOperators;
        return firstDateOperator;
      case FieldType.BOOLEAN:
        const [firstBooleanOperator] = this.booleanOperators;
        return firstBooleanOperator;
      default:
        break;
    }
  }

  setFieldType(column: string, tableConfig: TableConfig[]): FieldType {
    return tableConfig.find((colConfig) => colConfig.field === column)
      .fieldType;
  }

  deleteFilter(filterIndex: number): void {
    this.filters = this.filters.filter((_, index) => index !== filterIndex);

    this.emitFilters(this.filters);

    if (!this.filters.length) {
      this.addDefaultFilter();
      return;
    }
    this.checkValue();
  }

  applyFilters(): void {
    this.emitFilters(this.filters);
    this.closeMenu();
  }

  closeMenu(): void {
    if (this.filterMenu) {
      this.filterMenu.closeMenu();
    }
  }

  handleEnterKeyPress(event: KeyboardEvent): void {
    const isAdvFilterVisible = !!document.querySelector('.adv-menu-panel');

    if (event.key === 'Enter' && this.isApplyEnabled && isAdvFilterVisible) {
      this.forceCloseMatSelect();
  
      event.preventDefault();
      event.stopImmediatePropagation();
      this.applyFilters();
    }
  }

  private forceCloseMatSelect(): void {
    if (!this.matSelects) return;

    this.matSelects.forEach(select => {
      if (select.panelOpen) {
        select.close();
      }
    });
  }

  emitFilters(filters: AdvancedFilter[]) {
    if (this.filters.every(({ value }) => !value && value !== false)) {
      this.advancedFilterChanged.emit([]);
    } else {
      const treatedFilters = this.treatFilters(filters);
      this.advancedFilterChanged.emit(treatedFilters);
    }
  }

  treatFilters(filters: AdvancedFilter[]): AdvancedFilter[] {
    return filters.reduce((acc, cur) => {
      const { fieldType, value } = cur;
      return [
        ...acc,
        {
          ...cur,
          value:
            fieldType === FieldType.DATE || fieldType === FieldType.DATE_TIME
              ? new Date(<string>value).toISOString()
              : value,
        },
      ];
    }, []);
  }

  filterColumnChanged(
    event: MatSelectChange,
    changedFilterIndex: number
  ): void {
    const {
      value: { id },
    } = event;
    const colFieldType = this.setFieldType(id, this.tableConfig);
    const changedPropsObj = {
      column: id,
      fieldType: colFieldType,
      ...(colFieldType !== this.filters[changedFilterIndex].fieldType && {
        operator: this.setDefaultOperator(colFieldType),
      }),
      value: "",
    };

    this.filters = this.updateFilters(
      this.filters,
      changedFilterIndex,
      changedPropsObj
    );
    this.checkValue();
  }

  filterOperatorChanged(
    event: MatSelectChange,
    changedFilterIndex: number
  ): void {
    const { value } = event;
    const changedPropsObj = { operator: value };

    this.filters = this.updateFilters(
      this.filters,
      changedFilterIndex,
      changedPropsObj
    );
    this.checkValue();
  }

  filterInputChanged(event: string, changedFilterIndex: number): void {
    const changedPropsObj = { value: event };

    this.filters = this.updateFilters(
      this.filters,
      changedFilterIndex,
      changedPropsObj
    );
    this.checkValue();
  }

  updateFilters(
    filters: AdvancedFilter[],
    changedFilterIndex: number,
    changedPropsObj: object
  ): AdvancedFilter[] {
    return filters.reduce((accFilters, curFilter, index) => {
      return index === changedFilterIndex
        ? [...accFilters, { ...curFilter, ...changedPropsObj }]
        : [...accFilters, curFilter];
    }, []);
  }

  trackByFilter(index: number, item: AdvancedFilter): string {
    return item.id;
  }

  checkValue() {
    this.isApplyEnabled = true;
    if (this.filters) {
      this.isApplyEnabled = this.filters.every((filter) => {
        return filter.value !== "";
      });
    }
  }

  clearDate(filter: AdvancedFilter) {
    filter.value = '';
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.handleEnterKeyPress.bind(this));
  }
}